import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { errorSafe } from 'app/infrastructure/redux';
import { FieldItem, DomainNode } from '../../types';
import * as actions from './action-types';
import { codeItemToStoreValue, storeValueToCodeItem } from 'app/infrastructure/data/codes';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { selectSelectionReportInstanceState } from 'app/area/reports/selectors';
import { selectAggregationForSelectedReport, selectTargetApplicationForSelectedReport } from 'app/area/favourite/selectors';
import { actionCreators as favouriteActionCreatorsFactory } from 'app/area/favourite/actions';

export const domainNodeToComplexType = (dn: DomainNode): FieldItem => {
  return {
    value: dn.domain,
    description: dn.label,
    descendants: !!dn.children ? dn.children.map(domainNodeToComplexType) : undefined
  };
};

const fieldDoesntExistInDefaults = (fieldItems: FieldItem[], field: FieldItem) => {
  return !fieldItems.some(f => f.value === field.value);
};

export const actionCreators = (api: ReportCommonApi, instanceKey: string) => {

  const {
    setWorkingDefinitionTargetApplication
  } = favouriteActionCreatorsFactory(instanceKey);

  return {
    clearState: (): ActionDispatching<any, any> =>
      async (dispatch: any, getState: any) => {
        dispatch(new actions.ClearTargetState(instanceKey));
      },
    clearValue: (): ActionDispatching<any, any> =>
      async (dispatch: any, getState: any) => {
        dispatch(setWorkingDefinitionTargetApplication(undefined));
      },
    onSelectField: (selectedField: FieldItem): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: () => ApplicationState) => {
        const state = selectSelectionReportInstanceState(instanceKey)(getState());
        if (fieldDoesntExistInDefaults(state.targetFieldState.defaultFields, selectedField)) {
          dispatch(new actions.UpdateTargetDefaultFields(instanceKey, [...state.targetFieldState.defaultFields, selectedField]));
        }
        
        dispatch(setWorkingDefinitionTargetApplication(storeValueToCodeItem(selectedField)));
      }),
    loadDefaultFields: (): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: () => ApplicationState) => {
        
        dispatch(new actions.RequestTargetDefaultFields(instanceKey));
        
        const {
          aggregationType
        } = selectAggregationForSelectedReport(instanceKey)(getState());
        const aggregationTypeId = aggregationType?.v;

        if (aggregationTypeId) {
          const targetApplication = selectTargetApplicationForSelectedReport(instanceKey)(getState());
          const targetFieldItem = codeItemToStoreValue(targetApplication);

          const codetables = await api.loadTargetDefaultFields(aggregationTypeId as number);
          const options = codetables.data.rootCodes.map(codeItemToStoreValue);
          dispatch(new actions.ResponseTargetDefaultFields(instanceKey, [
            ...(options || []),
            ...(
              !options?.some(o => o?.value === targetFieldItem?.value) && targetFieldItem ? 
                [targetFieldItem] : 
                []
            )
          ]));
        } 
      }),
    loadFields: (query?: string): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: () => ApplicationState) => {
        const {
          aggregationType
        } = selectAggregationForSelectedReport(instanceKey)(getState());
        const aggregationTypeId = aggregationType?.v;

        if (aggregationTypeId) {
          const codetables = await api.loadTargetFields(aggregationTypeId as number, query);
          const options = codetables.data.rootCodes.map(codeItemToStoreValue);
          dispatch(new actions.ResponseTargetFields(instanceKey, options));
        } else {
          dispatch(new actions.ResponseTargetFields(instanceKey, []));
        }
      }),
    updateDefaultFields: (fieldItems: FieldItem[]): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        const targetApplication = selectTargetApplicationForSelectedReport(instanceKey)(getState());
        const targetFieldItem = codeItemToStoreValue(targetApplication);

        if (targetFieldItem?.value && fieldDoesntExistInDefaults(fieldItems, targetFieldItem)) {
          dispatch(new actions.UpdateTargetDefaultFields(instanceKey, [...fieldItems, targetFieldItem]));
        }
        else {
          dispatch(new actions.UpdateTargetDefaultFields(instanceKey, fieldItems));
        }
      })
  };
};
